import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  DefaultDadoField,
  UserImportDataSourceStatus,
  UserImportDataSourceUkg,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { getUkgFields } from '../../../../../Org/pages/Integrations/integrations/ukg/service';
import { CustomImportMappingFormWithAutocomplete } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.UKG;

// managerId -> manager.employeeId works because the backend matches UKG users
// on customFields.ukgEmployeeId, which holds the same UKG system key.
const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: DefaultDadoField.FIRST_NAME,
    otherKey: 'person.firstName'
  },
  {
    dadoKey: DefaultDadoField.LAST_NAME,
    otherKey: 'person.lastName'
  },
  {
    dadoKey: DefaultDadoField.EMAIL,
    otherKey: 'person.workEmail'
  },
  {
    dadoKey: DefaultDadoField.HIRE_DATE,
    otherKey: 'employment.hireDate'
  },
  {
    dadoKey: DefaultDadoField.MANAGER_ID,
    otherKey: 'manager.employeeId'
  }
];

// Only reachable from the import-source picker, which lists UKG only once the
// org is connected, so unlike ADP there is no config check here.
export const UkgSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [ukgFields, loadingFields, errorFields] = usePromise(() =>
    getUkgFields(orgId)
  );

  if (loadingFields) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (errorFields) {
    return <SomethingWentWrong height={150} />;
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>

        {ukgFields && (
          <CustomImportMappingFormWithAutocomplete
            userFields={userFields}
            integrationName={META.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            onChange={(mappings) => setMappings(mappings)}
            fields={ukgFields.map((f) => ({
              name: f.fieldPath,
              label: f.label
            }))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourceUkg> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.UKG,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings.filter((m) => !!m.otherKey),
                inferredOtherUsers: []
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const ImportSourceRowUkg = ({
  d
}: {
  d: Doc<UserImportDataSourceUkg>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
